import { Injectable } from "@angular/core";
import { StorageKeys } from "../constants/app-constants";
import { LocalStorageService } from "./local-storage.service";
import { defer, map, Observable, of, tap } from "rxjs";
import { catchError } from "rxjs/operators";
import { User } from "../models/user.interface";
import { ApiService } from "./api.service";
import { HttpClient } from "@angular/common/http";
import { environment } from "../../environments/environment";
import { ChangeUserBasicDetails } from "../models/change-user-basic-details.interface";
import { APIResponse } from "../models/standard-response.interface";
import { ɵFormGroupValue, ɵTypedOrUntyped } from "@angular/forms";

const profileEndpoint = "v1/profile"

@Injectable({
  providedIn: "root"
})
export class UserProfileService {
  private baseUrl: string = environment.apiBaseUrl;

  constructor(private localStorageService: LocalStorageService,
              private apiService: ApiService,
              private http: HttpClient) {
  }

  /**
   * Returns an Observable that, when subscribed, will
   * read & parse USER_PROFILE from localStorage (or emit null).
   */
  getUserProfileFromLocalStorage(): Observable<User | null> {
    return defer(() => {
      const raw = this.localStorageService.getItem(StorageKeys.USER_PROFILE);
      if (raw) {
        try {
          return of(JSON.parse(raw.toString()) as User);
        } catch (err) {
          console.warn('Corrupted USER_PROFILE JSON, refetching…', err);
        }
      } else {
        console.warn('USER_PROFILE not found in localStorage; fetching from API…');
      }
      // either it was missing or JSON.parse failed → hit the network
      return this.saveUserProfileToLocalStorage();
    }).pipe(
      catchError(err => {
        console.error('Failed to load USER_PROFILE', err);
        return of(null);
      })
    );
  }

  saveUserProfileToLocalStorage(): Observable<User | null> {
    return this.apiService.getMyProfile().pipe(
      map(res => res.data as User),
      tap(userProfile => this.localStorageService.setItem(StorageKeys.USER_PROFILE, JSON.stringify(userProfile))),
      catchError(err => {
        console.error("Failed to save user profile to localStorage", err);
        return of(null);
      })
    );
  }

  updateUserBasicDetails(userId: string | undefined,basicDetails: ChangeUserBasicDetails){
    return this.http.patch<APIResponse>(`${this.baseUrl}/${profileEndpoint}/${userId}/change-basic-details`, basicDetails)
  }

  sendEmailOtp(loggedInUserId: string | undefined, email: any) {
    return this.http.put<APIResponse>(`${this.baseUrl}/${profileEndpoint}/${loggedInUserId}/email-otp/send?newEmail=${email}`,{});
  }

  sendMobileOtp(loggedInUserId: string | undefined, mobile: any) {
    return this.http.put<APIResponse>(`${this.baseUrl}/${profileEndpoint}/${loggedInUserId}/mobile-otp/send?newMobile=${mobile}`,{});
  }

  changeEmail(loggedInUserId: string | undefined, req: {email: string, otp: string}) {
    return this.http.patch<APIResponse>(`${this.baseUrl}/${profileEndpoint}/${loggedInUserId}/change-email`, req);
  }

  changeMobile(loggedInUserId: string | undefined, req: {mobile: string, otp: string}) {
    return this.http.patch<APIResponse>(`${this.baseUrl}/${profileEndpoint}/${loggedInUserId}/change-mobile`, req);
  }

  updatePassword(loggedInUserId: string | undefined, passwordDto: {oldPassword: string, newPassword: string}) {
    return this.http.patch<APIResponse>(`${this.baseUrl}/${profileEndpoint}/${loggedInUserId}/change-password`, passwordDto);
  }
}
